import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import debounce from 'lodash/debounce';

import RXService from '@Services/RxService';
import { HEATMAP_OBSERVABLE_TOPICS, HEATMAP_SPLITTER_CHANGE_STATES } from '@Pages/Heatmap/constants';
import { ScrollMatrixSubject } from '@Pages/Heatmap/HeatmapObservableSubjects';
import scrollUtils from '@Pages/Heatmap/HeatmapTable/scrollUtils';
import BaseHeader from '@Pages/Heatmap/HeatmapTable/Header/BaseHeader';

const setElementTopOffset = (el, topOffset) => {
  if (!el) return;
  // eslint-disable-next-line no-param-reassign
  el.style.top = `${topOffset}px`;
};

function RowsHeader({ items, viewOptions, loaded, selectHeaderItem, selectedHeaderItems, hoveredItem, showStructure, handleHover }) {
  const selfRef = useRef();
  const containerRef = useRef();
  const [startPoint, setStartPoint] = useState(0);
  const [correction, setCorrection] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [cellsToShow, setCellsToShow] = useState(0);
  const [isVSplitterDragging, setIsVSplitterDragging] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedScrollHandler = useCallback(
    debounce((scrollTop) => {
      const firstRow = scrollUtils.calculateFirstRow(scrollTop, viewOptions.cellHeight);
      const firstRowCeil = Math.ceil(firstRow);
      const rowsOnViewportHalf = Math.ceil(headerHeight / 2 / viewOptions.cellHeight);
      let newRowStart = firstRowCeil - Math.ceil(cellsToShow / 2) + rowsOnViewportHalf;
      let newTopOffset =
        scrollTop -
        (Math.ceil(cellsToShow / 2) - firstRowCeil + firstRow - rowsOnViewportHalf) * scrollUtils.calculateCellHeight(viewOptions);

      if (newRowStart <= 0) {
        newRowStart = 0;
        newTopOffset = 0;
      }

      setCorrection(newTopOffset);
      setStartPoint(newRowStart);

      setElementTopOffset(containerRef.current, -scrollTop + newTopOffset);
    }, viewOptions.scrollDebounce),
    [cellsToShow, viewOptions.rowsAmount, viewOptions.cellHeight]
  );

  const onMatrixScrolled = useCallback(
    ({ scrollTop, scrollLeft }) => {
      setElementTopOffset(containerRef.current, -scrollTop + correction);
      debouncedScrollHandler(scrollTop, scrollLeft);
    },
    [correction, debouncedScrollHandler]
  );

  const terminateComponentSize = useCallback(() => {
    const _headerHeight = selfRef.current.offsetHeight;
    setHeaderHeight(_headerHeight);
    setCellsToShow(Math.ceil(_headerHeight / viewOptions.cellHeight) * viewOptions.viewBufferSize);
  }, [viewOptions.cellHeight, viewOptions.viewBufferSize]);

  const verticalSplitterChanged = useCallback((state) => {
    if (state === HEATMAP_SPLITTER_CHANGE_STATES.moved) {
      setIsVSplitterDragging(true);
    } else if (state === HEATMAP_SPLITTER_CHANGE_STATES.released) {
      setIsVSplitterDragging(false);
    }
  }, []);

  useEffect(() => {
    terminateComponentSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const rxSubscriptions = [RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.verticalSplitter, verticalSplitterChanged)];
    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
  }, [verticalSplitterChanged]);

  useEffect(() => {
    const rxSubscriptions = [ScrollMatrixSubject.subscribe({ next: onMatrixScrolled })];
    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
  }, [onMatrixScrolled]);

  return (
    <BaseHeader
      ref={selfRef}
      containerRef={containerRef}
      totalItems={viewOptions.rowsAmount}
      items={items}
      loaded={loaded}
      selectHeaderItem={selectHeaderItem}
      selectedHeaderItems={selectedHeaderItems}
      hoveredItem={hoveredItem}
      showStructure={showStructure}
      handleHover={handleHover}
      isRow
      startPoint={startPoint}
      cellsToShow={cellsToShow}
      style={{
        top: viewOptions.colsHeaderHeight,
        width: viewOptions.rowsHeaderWidth,
      }}
      headerClassName={classnames('heatmap__row-headers', {
        'splitter-v-dragging': isVSplitterDragging,
      })}
      headerContainerClassName="heatmap__row-headers__container"
      isVertical={false}
      height={viewOptions.cellHeight}
      width={viewOptions.rowsHeaderWidth}
    />
  );
}

RowsHeader.propTypes = {
  items: PropTypes.object.isRequired,
  viewOptions: PropTypes.object.isRequired,
  loaded: PropTypes.bool.isRequired,
  selectHeaderItem: PropTypes.func,
  selectedHeaderItems: PropTypes.array.isRequired,
  hoveredItem: PropTypes.object,
  showStructure: PropTypes.bool,
  handleHover: PropTypes.func,
};

export default RowsHeader;
